import React from 'react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    Button,
    Row,
    Col,
    Form,
    Card,
    Alert,
    Spinner,
    Badge,
    ProgressBar,
    ListGroup,
    Modal
} from 'react-bootstrap';
import bodyintelLogo from '../images/bodyintel-logo.png';

// Configure axios to include credentials for session cookies
axios.defaults.withCredentials = true;
// Remove global timeout to allow per-request timeouts to work properly
// axios.defaults.timeout = 20000;

function Homepage() {
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [questionStatus, setQuestionStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [recaptchaValue, setRecaptchaValue] = useState(null);

    const [sessionStatus, setSessionStatus] = useState(null);
    const [queryHistory, setQueryHistory] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(true);

    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [sessionCreated, setSessionCreated] = useState(false);
    const [lastQuestion, setLastQuestion] = useState('');

    const formRef = useRef(null);
    const abortControllerRef = useRef(null);
    const recaptchaRef = useRef(null);

    // Answer history state - stores all Q&A pairs
    const [answerHistory, setAnswerHistory] = useState([]);

    // Load session status on component mount
    useEffect(() => {
        loadSessionStatus();
    }, []);

    useEffect(() => {
        if (sessionStatus) {
            console.log('DEBUG: Session status updated:', {
                executed_query_count: sessionStatus.executed_query_count,
                max_queries_allowed_per_day: sessionStatus.max_queries_allowed_per_day
            });
        }
    }, [sessionStatus]);

    // Cleanup reCAPTCHA ref and abort controller on component unmount
    useEffect(() => {
        return () => {
            // Cancel any pending requests first
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Safely cleanup reCAPTCHA refs
            try {
                recaptchaRef.current = null;
                if (window.recaptchaRef) {
                    delete window.recaptchaRef;
                }
            } catch (error) {
                console.warn('Error cleaning up reCAPTCHA ref:', error);
            }
        };
    }, []);


    const loadSessionStatus = async () => {
        try {
            const response = await axios.get('https://api.bodyintel.ai/session/status');
            const sessionData = {
                ...response.data,
                executed_query_count: parseInt(response.data.executed_query_count) || 0,
                max_queries_allowed_per_day: parseInt(response.data.max_queries_allowed_per_day) || 50
            };
            setSessionStatus(sessionData);
            setSessionCreated(true);
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.detail === 'SESSION_NOT_FOUND') {
                setSessionStatus(null);
                setSessionCreated(false);
                setQuestionStatus('Please click on reCAPTCHA and then Create Session');
                return;
            }
        } finally {
            setSessionLoading(false);
        }
    };

    const createSession = async () => {
        try {
            setIsCreatingSession(true);
            // Create session explicitly with recaptcha token
            const response = await axios.post(
                'https://api.bodyintel.ai/session/create',
                { recaptcha_token: recaptchaValue },
                { withCredentials: true }
            );
            const sessionData = {
                ...response.data,
                executed_query_count: parseInt(response.data.executed_query_count) || 0,
                max_queries_allowed_per_day: parseInt(response.data.max_queries_allowed_per_day) || 50
            };
            setSessionStatus(sessionData);
            setSessionCreated(true);
            setQuestionStatus('Session initialized');
            // reset and clear recaptcha after successful session creation
            setRecaptchaValue(null);
            //await safeRecaptchaReset();
        } catch (error) {
            setQuestionStatus(error.response?.data?.detail);
        } finally {
            setIsCreatingSession(false);
        }
    };

    const loadQueryHistory = async () => {
        try {
            const response = await axios.get('https://api.bodyintel.ai/session/queries');
            setQueryHistory(response.data.queries || []);
        } catch (error) {
            console.error('Error loading query history:', error);
        }
    };

    const handleAnswer = (newAnswer, currentQuestion) => {
        setAnswer(newAnswer);

        // Add the new Q&A pair to the history (latest on top)
        const newQAPair = {
            question: currentQuestion,
            answer: newAnswer,
            timestamp: new Date().toISOString()
        };

        setAnswerHistory(prevHistory => [newQAPair, ...prevHistory]);
    };

    const handleRecaptchaChange = (value) => {
        setRecaptchaValue(value);
    };

    const handleRecaptchaError = () => {
        setQuestionStatus('reCAPTCHA failed to load. Please refresh and try again.');
    };

    const handleRecaptchaExpired = () => {
        setRecaptchaValue(null);
        setQuestionStatus('reCAPTCHA expired. Please complete it again.');
    };

    const safeRecaptchaReset = async () => {
        try {
            const recaptcha = recaptchaRef.current || window.recaptchaRef;
            if (recaptcha && typeof recaptcha.reset === 'function') {
                console.log('Safe reCAPTCHA reset start');

                // Create a promise that resolves after reset or times out
                const resetPromise = new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error('reCAPTCHA reset timeout'));
                    }, 5000); // 5 second timeout for reset

                    try {
                        recaptcha.reset();
                        clearTimeout(timeout);
                        resolve();
                    } catch (error) {
                        clearTimeout(timeout);
                        reject(error);
                    }
                });

                await resetPromise;
                console.log('Safe reCAPTCHA reset completed');
            }
        } catch (error) {
            console.warn('reCAPTCHA reset failed:', error);
            // Don't throw error, just log it - the app should continue working
        }
    };

    const retryLastQuestion = () => {
        if (lastQuestion.trim()) {
            setQuestion(lastQuestion);
            // Trigger form submission
            setTimeout(() => {
                if (formRef.current && typeof formRef.current.requestSubmit === 'function') {
                    formRef.current.requestSubmit();
                }
            }, 100);
        }
    };

    const handleQuestion = async (event) => {
        event.preventDefault();
        if (!sessionStatus) {
            setQuestionStatus('Please click on Create Session before asking');
            return;
        }
        if (!question.trim()) {
            setQuestionStatus('Please enter a question');
            return;
        }

        // Cancel any existing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        setQuestionStatus('Processing your question...');
        setAnswer('');
        setLastQuestion(question); // Store the question for potential retry

        try {
            const response = await axios.post(
                'https://api.bodyintel.ai/process_query/',
                {
                    'question': question,
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    timeout: 120000, // 2 minute timeout for AI processing
                    signal: abortControllerRef.current.signal
                }
            );

            const updatedAnswer = response.data.response.answer;
            handleAnswer(updatedAnswer, question);
            setQuestionStatus('Question processed successfully');
            setQuestion(''); // Clear the question input after successful submission
            setRecaptchaValue(null); // Clear reCAPTCHA for next query

            // Reset reCAPTCHA widget safely
            //await safeRecaptchaReset();

            // Update session status if provided
            if (response.data.session_info) {
                setSessionStatus(prev => {
                    const updated = {
                        ...prev,
                        executed_query_count: parseInt(response.data.session_info.executed_query_count),
                        max_queries_allowed_per_day: parseInt(response.data.session_info.max_queries_allowed_per_day)
                    };
                    return updated;
                });
            } else {
                // If no session_info provided, reload session status
                console.log('DEBUG: No session_info provided, reloading session status');
                await loadSessionStatus();
            }


        } catch (error) {
            console.error('Question POST error: ', error);

            // Handle request cancellation
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                setQuestionStatus('Request was cancelled');
                return;
            }

            // Handle session not found
            if (error.response?.status === 400 && error.response?.data?.detail === 'SESSION_NOT_FOUND') {
                setSessionStatus(null);
                setSessionCreated(false);
                setQuestionStatus('Session expired. Please create a new session.');
                return;
            }

            // Handle timeout errors
            else if (error.code === 'ECONNABORTED' ||
                     (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout')) ||
                     error.code === 'ETIMEDOUT') {
                setQuestionStatus('Request timed out. The API is taking longer than expected. Please try again.');
            }

            // Handle rate limiting
            else if (error.response?.status === 429) {
                setQuestionStatus('Rate limit exceeded. Please wait before making more queries.');
            }

            // Handle network errors
            else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
                setQuestionStatus('Network error. Please check your connection and try again.');
            }

            // Handle server errors
            else if (error.response?.status >= 500) {
                setQuestionStatus(error.response?.data?.detail);
            }

            // Generic error
            else {
                setQuestionStatus('Error encountered while processing your question. Please try again.');
            }
        } finally {
            setIsLoading(false);
            // Clear the abort controller reference
            abortControllerRef.current = null;
        }
    };

    const getQueryProgress = () => {
        if (!sessionStatus) return 0;
        const queryCount = parseInt(sessionStatus.executed_query_count) || 0;
        const maxQueries = parseInt(sessionStatus.max_queries_allowed_per_day) || 50;
        return (queryCount / maxQueries) * 100;
    };

    const getProgressVariant = () => {
        const progress = getQueryProgress();
        if (progress >= 90) return 'danger';
        if (progress >= 75) return 'warning';
        return 'success';
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <div className="w-100">
            <Row className="justify-content-center">
                <Col lg={8} md={10} sm={12}>
                    {/* Header Section */}
                    <Row className="mb-4">
                        <Col>
                            <div className="text-center">
                                <div className="mb-3">
                                    <img
                                        src={bodyintelLogo}
                                        alt="BodyIntel Logo"
                                        className="img-fluid"
                                        style={{ maxHeight: '80px', width: 'auto' }}
                                    />
                                </div>
                                <h1 className="display-4 mb-3">
                                    <Badge bg="primary">BodyIntel</Badge> AI Assistant
                                </h1>
                                <p className="lead text-muted">
                                BodyIntel uses pubmed and other research data to answer your questions.
                                Answers and information provided could be wrong and are provided for informational purposes only.
                                Any information provided should not be used for medical/health advice.
                                <Link to="/terms" className="text-decoration-none fw-bold">
                                    Check Terms and Conditions.
                                </Link>
                                </p>
                            </div>
                        </Col>
                    </Row>

                    {/* Session Status Section */}
                    {sessionStatus && (
                        <Row className="mb-4">
                            <Col>
                                <Card className="shadow-sm">
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-3">
                                                    <strong>Queries Executed Today:</strong> {sessionStatus.executed_query_count}
                                                    <ProgressBar
                                                        variant={getProgressVariant()}
                                                        now={getQueryProgress()}
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <strong>Max Allowed Per Day:</strong> {sessionStatus.max_queries_allowed_per_day}
                                                </div>
                                            </Col>
                                            <Col md={6} className="d-flex align-items-start justify-content-md-end">
                                                {sessionCreated ? (
                                                    <Badge bg="success" className="align-self-start">Session Active</Badge>
                                                ) : (
                                                    <div className="d-grid">
                                                        <div className="mb-2 d-flex justify-content-center">
                                                            <ReCAPTCHA
                                                                ref={(ref) => {
                                                                    if (ref) {
                                                                        recaptchaRef.current = ref;
                                                                        window.recaptchaRef = ref;
                                                                    }
                                                                }}
                                                                sitekey="6LcxOLIrAAAAAFTIjRTSTsZ_c45SzurB17kgBhmf"
                                                                onChange={handleRecaptchaChange}
                                                                onErrored={handleRecaptchaError}
                                                                onExpired={handleRecaptchaExpired}
                                                                disabled={isCreatingSession}
                                                            />
                                                        </div>
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={createSession}
                                                            disabled={isCreatingSession || sessionCreated || !recaptchaValue}
                                                        >
                                                            {isCreatingSession ? (
                                                                <>
                                                                    <Spinner
                                                                        as="span"
                                                                        animation="border"
                                                                        size="sm"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                        className="me-2"
                                                                    />
                                                                    Creating Session...
                                                                </>
                                                            ) : (
                                                                <>Create Session</>
                                                            )}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {!sessionStatus && (
                        <Row className="mb-4">
                            <Col>
                                {!sessionCreated && (
                                    <div className="d-grid">
                                        <div className="mb-2 d-flex justify-content-center">
                                            <ReCAPTCHA
                                                ref={(ref) => {
                                                    if (ref) {
                                                        recaptchaRef.current = ref;
                                                        window.recaptchaRef = ref;
                                                    }
                                                }}
                                                sitekey="6LcxOLIrAAAAAFTIjRTSTsZ_c45SzurB17kgBhmf"
                                                onChange={handleRecaptchaChange}
                                                onErrored={handleRecaptchaError}
                                                onExpired={handleRecaptchaExpired}
                                                disabled={isCreatingSession}
                                            />
                                        </div>
                                        <Button
                                            variant="primary"
                                            onClick={createSession}
                                            disabled={isCreatingSession || sessionCreated || !recaptchaValue}
                                        >
                                            {isCreatingSession ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Creating Session...
                                                </>
                                            ) : (
                                                <>Create Session</>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}

                    {/* Question Input Section */}
                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow-sm">
                                <Card.Header className="bg-primary text-white">
                                    <h4 className="mb-0">
                                        <i className="fas fa-question-circle me-2"></i>
                                        Ask your question
                                    </h4>
                                </Card.Header>
                                <Card.Body>
                                    <Form onSubmit={handleQuestion} ref={formRef}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="questionInput">
                                            </Form.Label>
                                            <Form.Control
                                                id="questionInput"
                                                as="textarea"
                                                rows={3}
                                                placeholder="Enter your health or fitness related question here..."
                                                value={question}
                                                onChange={e => setQuestion(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        if (formRef.current && typeof formRef.current.requestSubmit === 'function') {
                                                            formRef.current.requestSubmit();
                                                        }
                                                    }
                                                }}
                                                className="w-100"
                                                style={{ width: '100%' }}
                                                disabled={isLoading || (sessionStatus && parseInt(sessionStatus.executed_query_count) >= parseInt(sessionStatus.max_queries_allowed_per_day))}
                                            />
                                        </Form.Group>


                                        <div className="d-grid">
                                            {isLoading ? (
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="primary"
                                                        size="lg"
                                                        disabled
                                                        className="flex-grow-1"
                                                    >
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                            className="me-2"
                                                        />
                                                        Processing...
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="lg"
                                                        onClick={() => {
                                                            if (abortControllerRef.current) {
                                                                abortControllerRef.current.abort();
                                                            }
                                                        }}
                                                    >
                                                        <i className="fas fa-times me-2"></i>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="primary"
                                                    size="lg"
                                                    type="submit"
                                                    disabled={
                                                        !sessionStatus ||
                                                        !question.trim() ||
                                                        (sessionStatus &&
                                                         parseInt(sessionStatus.executed_query_count) >= parseInt(sessionStatus.max_queries_allowed_per_day))
                                                    }
                                                >
                                                    <i className="fas fa-paper-plane me-2"></i>
                                                    Ask BodyIntel AI
                                                </Button>
                                            )}
                                        </div>
                                        <div></div>

                                        {sessionStatus && parseInt(sessionStatus.executed_query_count) >= parseInt(sessionStatus.max_queries_allowed_per_day) && (
                                            <Alert variant="warning" className="mt-3">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                <strong>Limit Reached:</strong> You have reached the max queries limit for the day.
                                            </Alert>
                                        )}
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Status Section */}
                    {questionStatus && (
                        <Row className="mb-4">
                            <Col>
                                <Alert
                                    variant={questionStatus.includes('Error') || questionStatus.includes('timed out') || questionStatus.includes('cancelled') ? 'danger' :
                                            questionStatus.includes('successfully') ? 'success' : 'info'}
                                    className="mb-0"
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas ${
                                                questionStatus.includes('Error') || questionStatus.includes('timed out') || questionStatus.includes('cancelled') ? 'fa-exclamation-triangle' :
                                                questionStatus.includes('successfully') ? 'fa-check-circle' : 'fa-info-circle'
                                            } me-2`}></i>
                                            <strong>Status:</strong> <span style={{ color: 'red' }}>{questionStatus}</span>
                                        </div>
                                        {(questionStatus.includes('Error') || questionStatus.includes('timed out') || questionStatus.includes('cancelled')) && lastQuestion.trim() && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={retryLastQuestion}
                                                disabled={isLoading}
                                            >
                                                <i className="fas fa-redo me-1"></i>
                                                Retry
                                            </Button>
                                        )}
                                    </div>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {/* Answer History Section */}
                    {answerHistory.length > 0 && (
                        <Row>
                            <Col>
                                <Card className="shadow-sm">
                                    <Card.Header className="bg-success text-white">
                                        <div className="d-flex justify-content-start align-items-left">
                                            <h4 className="mb-0">
                                                <i className="fas fa-lightbulb me-2"></i>
                                                Question & Answer History
                                            </h4>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="answer-history text-start" style={{ textAlign: 'left' }}>
                                            {answerHistory.map((qaPair, index) => (
                                                <div key={index} className={`mb-4 pb-4 ${index < answerHistory.length - 1 ? 'border-bottom' : ''}`}>
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <small className="text-muted">
                                                            {formatTimestamp(qaPair.timestamp)}
                                                        </small>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong>Question: </strong> {qaPair.question}
                                                    </div>
                                                    <div className="answer-content">
                                                        <strong>Answer:</strong>
                                                        <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                                            {qaPair.answer}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export default Homepage;